import React from 'react';
import { 
  Box, 
  Typography, 
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormControl,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { Account } from '../services/AccountsService';

interface TablePaginationProps {
  page: number;
  rowsPerPage: number;
  totalItems: number;
  paginatedAccounts: Account[];
  serverPagination: boolean;
  handlePageChange: (page: number) => void;
  handleChangeRowsPerPage: (event: SelectChangeEvent<number>) => void;
  getPaginationInfo: () => string;
}

const TablePagination: React.FC<TablePaginationProps> = ({
  page,
  rowsPerPage,
  totalItems,
  paginatedAccounts,
  serverPagination,
  handlePageChange,
  handleChangeRowsPerPage,
  getPaginationInfo
}) => {
  const theme = useTheme();
  
  return (
    <Box 
      sx={{ 
        px: 3, 
        py: 2, 
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between',
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
        backgroundColor: theme.palette.background.default
      }}
    >
      <Box display="flex" alignItems="center">
        <Typography 
          variant="body2" 
          sx={{ 
            mr: 2,
            color: alpha(theme.palette.text.primary, 0.7),
            fontWeight: 500
          }}
        >
          Items per page:
        </Typography>
        <FormControl 
          size="small" 
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }
          }}
        >
          <Select
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            sx={{ 
              height: 36, 
              minWidth: 70,
              fontWeight: 500,
              '& .MuiSelect-select': { 
                py: 0.7, 
                px: 1.5 
              } 
            }}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={100}>100</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box display="flex" alignItems="center">
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 500,
            color: alpha(theme.palette.text.primary, 0.7) 
          }}
        >
          {getPaginationInfo()}
        </Typography>
        <Box ml={1.5} display="flex" alignItems="center">
          <Tooltip title="Previous page">
            <span>
              <IconButton 
                disabled={page === 0} 
                onClick={() => handlePageChange(page - 1)}
                sx={{ 
                  mx: 0.5,
                  transition: 'all 0.2s',
                  color: page === 0 ? undefined : theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    transform: 'scale(1.05)'
                  }
                }}
                size="small"
              >
                <NavigateBeforeIcon />
              </IconButton>
            </span>
          </Tooltip>
          
          <Tooltip title="Next page">
            <span>
              <IconButton 
                disabled={
                  serverPagination 
                    ? (page + 1) * rowsPerPage >= totalItems
                    : (page + 1) * rowsPerPage >= totalItems || paginatedAccounts.length < rowsPerPage
                } 
                onClick={() => handlePageChange(page + 1)}
                sx={{ 
                  mx: 0.5,
                  transition: 'all 0.2s',
                  color: (serverPagination 
                    ? (page + 1) * rowsPerPage >= totalItems
                    : (page + 1) * rowsPerPage >= totalItems || paginatedAccounts.length < rowsPerPage) 
                      ? undefined : theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    transform: 'scale(1.05)'
                  }
                }}
                size="small"
              >
                <NavigateNextIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default TablePagination;